import * as e from './enums';
import { Observable } from 'rxjs';
import { Directive, EventEmitter, InjectionToken, Input, Output, Type } from '@angular/core';
import { PhoneInterface } from '@vendasta/crm';

@Directive()
export abstract class GalaxyAbstractFilterInput {
  @Input() initialValue!: GalaxyFilterValueInterface[];
  @Input() operator?: e.GalaxyFilterOperator;
  @Output() valueChanges!: EventEmitter<GalaxyFilterValueInterface[]>;

  abstract clearValue(): void;
}

@Directive()
export abstract class GalaxyAbstractFilterChip {
  @Input() initialValue!: GalaxyFilterValueInterface[];
}

export interface GalaxyFilterChipDependencies {
  // listObjectFilters will return the list of filters that can be applied. They should be filtered by the searchTerm
  listObjectFilters(searchTerm: string): Observable<GalaxyFilterDefinitionInterface[]>;

  // getFieldOptions will return the list of options for a given fieldId.
  // This allows you to autocomplete a filter option for a user.
  getFieldOptions?(fieldId: string, search: string): Observable<string[]>;

  // getInitialAppliedFilters will return the default list of filters that should be displayed.
  // This allows you to have an initial filter state
  getInitialAppliedFilters?(): Promise<GalaxyFilterInterface[]>;
  setInitialAppliedFilters?(filters: GalaxyFilterInterface[]): void;

  // getFilterInputOverrides?(): Observable<GalaxyFilterInputOverride[]>;
  // This allows you to override the search box for any field
  // The filter input implementation will still have to abide by the operators allowed for that filter
  getFilterInputOverrides?(): Observable<GalaxyFilterInputOverride[]>;
}

// GalaxyFilterChipInjectionToken is the external token used by consumers to specify the dependencies.
export const GalaxyFilterChipInjectionToken = new InjectionToken<GalaxyFilterChipDependencies>(
  '[Galaxy]: dependencies for the table filter',
);

// InnerGalaxyFilterChipInjectionToken is used by the library to have defaults provided for the method implementations (NullObjectPattern).
export const InnerGalaxyFilterChipInjectionToken = new InjectionToken<Required<GalaxyFilterChipDependencies>>(
  '[Galaxy]: inner dependencies for the table filter where all properties are required',
);

export interface GalaxyFilterDefinitionInterface {
  fieldId: string;
  type?: e.GalaxyFilterType;
  hasFilterOptions?: boolean;
  supportedOperators?: e.GalaxyFilterOperator[];
  fieldName?: string;
  // presets cannot be removed, but can be cleared
  preset?: boolean;
  // locked value filers cannot have their values removed after they are set
  lockedValue?: boolean;
}
export interface GalaxyFilterValueInterface {
  boolean?: boolean;
  integer?: number;
  float?: number;
  string?: string;
  date?: Date;
  dateDefault?: e.GalaxyDateDefault;
  phone?: PhoneInterface;
}

export interface GalaxyFilterInterface {
  fieldId: string;
  filterId?: string; // unique for each instance of filter on frontend
  operator: e.GalaxyFilterOperator;
  values?: GalaxyFilterValueInterface[];
  floatTolerance?: number;
  definition?: GalaxyFilterDefinitionInterface;
}

export const GalaxySymbolsFilterOperators: { [key in e.GalaxyFilterOperator]: string } = {
  [e.GalaxyFilterOperator.FILTER_OPERATOR_INVALID]: 'GALAXY.FILTER.CHIP.SYMBOL.INVALID',
  [e.GalaxyFilterOperator.FILTER_OPERATOR_IS_EMPTY]: 'GALAXY.FILTER.CHIP.SYMBOL.IS_EMPTY',
  [e.GalaxyFilterOperator.FILTER_OPERATOR_IS_NOT_EMPTY]: 'GALAXY.FILTER.CHIP.SYMBOL.IS_NOT_EMPTY',
  [e.GalaxyFilterOperator.FILTER_OPERATOR_IS]: 'GALAXY.FILTER.CHIP.SYMBOL.IS',
  [e.GalaxyFilterOperator.FILTER_OPERATOR_IS_NOT]: 'GALAXY.FILTER.CHIP.SYMBOL.IS_NOT',
  [e.GalaxyFilterOperator.FILTER_OPERATOR_IS_ANY]: 'GALAXY.FILTER.CHIP.SYMBOL.IS_ANY',
  [e.GalaxyFilterOperator.FILTER_OPERATOR_IS_NOT_ANY]: 'GALAXY.FILTER.CHIP.SYMBOL.IS_NOT_ANY',
  [e.GalaxyFilterOperator.FILTER_OPERATOR_IS_ALL]: 'GALAXY.FILTER.CHIP.SYMBOL.IS_ALL',
  [e.GalaxyFilterOperator.FILTER_OPERATOR_IS_NOT_ALL]: 'GALAXY.FILTER.CHIP.SYMBOL.IS_NOT_ALL',
  [e.GalaxyFilterOperator.FILTER_OPERATOR_CONTAINS]: 'GALAXY.FILTER.CHIP.SYMBOL.CONTAINS',
  [e.GalaxyFilterOperator.FILTER_OPERATOR_DOES_NOT_CONTAIN]: 'GALAXY.FILTER.CHIP.SYMBOL.DOES_NOT_CONTAIN',
  [e.GalaxyFilterOperator.FILTER_OPERATOR_IS_EQUAL_TO]: 'GALAXY.FILTER.CHIP.SYMBOL.IS_EQUAL_TO',
  [e.GalaxyFilterOperator.FILTER_OPERATOR_IS_NOT_EQUAL_TO]: 'GALAXY.FILTER.CHIP.SYMBOL.IS_NOT_EQUAL_TO',
  [e.GalaxyFilterOperator.FILTER_OPERATOR_IS_GREATER_THAN]: 'GALAXY.FILTER.CHIP.SYMBOL.IS_GREATER_THAN',
  [e.GalaxyFilterOperator.FILTER_OPERATOR_IS_GREATER_THAN_OR_EQUAL_TO]:
    'GALAXY.FILTER.CHIP.SYMBOL.IS_GREATER_THAN_OR_EQUAL_TO',
  [e.GalaxyFilterOperator.FILTER_OPERATOR_IS_LESS_THAN]: 'GALAXY.FILTER.CHIP.SYMBOL.IS_LESS_THAN',
  [e.GalaxyFilterOperator.FILTER_OPERATOR_IS_LESS_THAN_OR_EQUAL_TO]:
    'GALAXY.FILTER.CHIP.SYMBOL.IS_LESS_THAN_OR_EQUAL_TO',
  [e.GalaxyFilterOperator.FILTER_OPERATOR_IS_BEFORE]: 'GALAXY.FILTER.CHIP.SYMBOL.IS_BEFORE',
  [e.GalaxyFilterOperator.FILTER_OPERATOR_IS_BEFORE_OR_ON]: 'GALAXY.FILTER.CHIP.SYMBOL.IS_BEFORE_OR_ON',
  [e.GalaxyFilterOperator.FILTER_OPERATOR_IS_AFTER]: 'GALAXY.FILTER.CHIP.SYMBOL.IS_AFTER',
  [e.GalaxyFilterOperator.FILTER_OPERATOR_IS_AFTER_OR_ON]: 'GALAXY.FILTER.CHIP.SYMBOL.IS_AFTER_OR_ON',
  [e.GalaxyFilterOperator.FILTER_OPERATOR_IS_BETWEEN]: 'GALAXY.FILTER.CHIP.SYMBOL.IS_BETWEEN',
  [e.GalaxyFilterOperator.FILTER_OPERATOR_IS_NOT_BETWEEN]: 'GALAXY.FILTER.CHIP.SYMBOL.IS_NOT_BETWEEN',
  [e.GalaxyFilterOperator.FILTER_OPERATOR_IS_VALID]: 'GALAXY.FILTER.CHIP.OPERATOR.IS_VALID',
  [e.GalaxyFilterOperator.FILTER_OPERATOR_IS_NOT_VALID]: 'GALAXY.FILTER.CHIP.OPERATOR.IS_NOT_VALID',
};

export const GalaxyStringFilterValue: { [key in e.GalaxyDateDefault]: string } = {
  [e.GalaxyDateDefault.DATE_DEFAULT_INVALID]: 'GALAXY.FILTER.CHIP.DATE_DEFAULT.INVALID',
  [e.GalaxyDateDefault.DATE_DEFAULT_TODAY]: 'GALAXY.FILTER.CHIP.DATE_DEFAULT.TODAY',
  [e.GalaxyDateDefault.DATE_DEFAULT_YESTERDAY]: 'GALAXY.FILTER.CHIP.DATE_DEFAULT.YESTERDAY',
  [e.GalaxyDateDefault.DATE_DEFAULT_TOMORROW]: 'GALAXY.FILTER.CHIP.DATE_DEFAULT.TOMORROW',
  [e.GalaxyDateDefault.DATE_DEFAULT_THIS_WEEK]: 'GALAXY.FILTER.CHIP.DATE_DEFAULT.THIS_WEEK',
  [e.GalaxyDateDefault.DATE_DEFAULT_LAST_WEEK]: 'GALAXY.FILTER.CHIP.DATE_DEFAULT.LAST_WEEK',
  [e.GalaxyDateDefault.DATE_DEFAULT_NEXT_WEEK]: 'GALAXY.FILTER.CHIP.DATE_DEFAULT.NEXT_WEEK',
  [e.GalaxyDateDefault.DATE_DEFAULT_THIS_MONTH]: 'GALAXY.FILTER.CHIP.DATE_DEFAULT.THIS_MONTH',
  [e.GalaxyDateDefault.DATE_DEFAULT_LAST_MONTH]: 'GALAXY.FILTER.CHIP.DATE_DEFAULT.LAST_MONTH',
  [e.GalaxyDateDefault.DATE_DEFAULT_NEXT_MONTH]: 'GALAXY.FILTER.CHIP.DATE_DEFAULT.NEXT_MONTH',
  [e.GalaxyDateDefault.DATE_DEFAULT_THIS_QUARTER]: 'GALAXY.FILTER.CHIP.DATE_DEFAULT.THIS_QUARTER',
  [e.GalaxyDateDefault.DATE_DEFAULT_LAST_QUARTER]: 'GALAXY.FILTER.CHIP.DATE_DEFAULT.LAST_QUARTER',
  [e.GalaxyDateDefault.DATE_DEFAULT_NEXT_QUARTER]: 'GALAXY.FILTER.CHIP.DATE_DEFAULT.NEXT_QUARTER',
  [e.GalaxyDateDefault.DATE_DEFAULT_THIS_YEAR]: 'GALAXY.FILTER.CHIP.DATE_DEFAULT.THIS_YEAR',
  [e.GalaxyDateDefault.DATE_DEFAULT_LAST_YEAR]: 'GALAXY.FILTER.CHIP.DATE_DEFAULT.LAST_YEAR',
  [e.GalaxyDateDefault.DATE_DEFAULT_NEXT_YEAR]: 'GALAXY.FILTER.CHIP.DATE_DEFAULT.NEXT_YEAR',
};

export interface GalaxyFilterInputOverride {
  fieldId: string;
  filterInput: Type<GalaxyAbstractFilterInput>;
  filterChip: Type<GalaxyAbstractFilterChip>;
}
